import React, { useEffect, useRef, useState } from "react"
import { SparklesIcon, PaperAirplaneIcon, PlusIcon, CheckIcon, ClockIcon, HashtagIcon, ChartBarIcon, TableCellsIcon, Bars3BottomLeftIcon, Squares2X2Icon } from "@heroicons/react/24/outline"
import Modal from "aldoo-ra/Modal"
import { AdvisorStart, AdvisorAsk, AdvisorReset } from "./api"
import MetricCard from "./cards/MetricCard"
import ChartCard from "./cards/ChartCard"
import TableCard from "./cards/TableCard"
import SegmentsCard from "./cards/SegmentsCard"

// money and percentages the model left plain: set in bold like the figures it did mark
// (a currency symbol before or after the amount — $, €, £, ¥, лв. — or a percent)
const FIGURE = /([+−-]?[$€£¥₹₽₺₩]\s?\d[\d\s.,]*\d|[+−-]?[$€£¥₹₽₺₩]\s?\d|[+−-]?\d[\d\s.,]*\d\s?(?:[$€£¥₹₽₺₩]|лв\.?|[A-Z]{3}\b)|[+−-]?\d[\d.,]*\s?%)/g
const markFigures = (line) =>
  line
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part) => (part.startsWith("**") ? part : part.replace(FIGURE, "**$1**")))
    .join("")

/** **bold**, "- " lists and paragraphs: all the advisor is asked to write. */
function Prose({ text }) {
  const inline = (line, key) =>
    markFigures(line).split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? <strong key={`${key}-${i}`} className="font-semibold text-admin_text">{part.slice(2, -2)}</strong> : <React.Fragment key={`${key}-${i}`}>{part}</React.Fragment>
    )
  const blocks = []
  let list = null
  let table = null
  String(text || "")
    .trim()
    .split("\n")
    .forEach((raw, i) => {
      const line = raw.trim()
      // a table the model wrote in text (it is asked not to): drawn as one, not as pipes
      if (/^\|.*\|$/.test(line)) {
        list = null
        if (/^\|[\s:|-]+\|$/.test(line)) return
        const cells = line.slice(1, -1).split("|").map((c, j) => inline(c.trim(), `${i}-${j}`))
        if (!table) blocks.push((table = { type: "table", head: cells, rows: [] }))
        else table.rows.push(cells)
        return
      }
      if (line) table = null
      if (/^[-*•]\s+/.test(line)) {
        if (!list) blocks.push((list = { type: "ul", items: [] }))
        list.items.push(inline(line.replace(/^[-*•]\s+/, ""), i))
        return
      }
      list = null
      if (line) blocks.push({ type: "p", content: inline(line, i) })
    })
  return (
    <div className="flex flex-col gap-2 text-[14px] leading-relaxed text-admin_text">
      {blocks.map((b, i) =>
        b.type === "table" ? (
          <div key={i} className="overflow-x-auto rounded-lg border border-admin_bordered">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-admin_neutral_1/60">
                  {b.head.map((cell, j) => <th key={j} className="px-3 py-1.5 text-left font-semibold text-admin_text_secondary">{cell}</th>)}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, r) => (
                  <tr key={r} className="border-t border-admin_bordered">
                    {row.map((cell, j) => <td key={j} className="px-3 py-1.5 tabular-nums">{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : b.type === "ul" ? (
          <ul key={i} className="list-disc pl-5 flex flex-col gap-1">
            {b.items.map((item, j) => <li key={j}>{item}</li>)}
          </ul>
        ) : (
          <p key={i} className="m-0">{b.content}</p>
        )
      )}
    </div>
  )
}

/** A card the advisor showed: drawn by the dashboard's own card, with "Add to dashboard". */
function AdvisorCard({ entry, catalog, onAdd, t }) {
  const [added, setAdded] = useState(false)
  const { card, data, period, comparison, timeZone } = entry
  const metricDef = catalog?.metrics?.find((m) => m.id === card.metric)
  const tableDef = catalog?.tables?.find((t) => t.id === card.table)
  const comparing = !!comparison
  const props = { card, data, loading: false }
  return (
    <div className="flex flex-col gap-1.5" data-testid="advisor-card">
      {card.type === "metric" && <MetricCard {...props} def={metricDef} comparing={comparing} />}
      {card.type === "chart" && <ChartCard {...props} def={metricDef} period={period} comparison={comparison} timeZone={timeZone} dimensions={catalog?.dimensions} />}
      {card.type === "table" && <TableCard {...props} def={tableDef} />}
      {card.type === "segments" && <SegmentsCard {...props} def={metricDef} comparing={comparing} dimensions={catalog?.dimensions} />}
      <button
        type="button"
        disabled={added}
        onClick={() => {
          onAdd(entry)
          setAdded(true)
        }}
        className={`self-end inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold ${added ? "text-admin_success" : "text-admin_primary hover:bg-admin_primary/10"}`}
        data-testid="advisor-add-card"
      >
        {added ? <CheckIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
        {added ? t.added : t.add}
      </button>
    </div>
  )
}

// the drawer's own words, in the advisor's two languages
const TEXT = {
  en: {
    hi: (name) => `Hi, I'm ${name}`,
    intro: "Ask me anything about your sales, orders, customers, products and stock. I answer from your figures, for the period and filters on your dashboard unless you say otherwise.",
    disclaimer: "AI can make mistakes: the cards show the figures it used.",
    resume: "Resume most recent chat",
    newChat: "New chat",
    placeholder: "Ask about your sales, orders, products…",
    unavailable: "The advisor is not available right now. Please try again later.",
    setupTitle: "Set up the advisor first",
    setupAdmin: (model, key) => `Its AI model${model ? ` (${model})` : ""} has no API key yet. Add ${key || "the model's API key"} in Admin → ParamIO, then open the advisor again.`,
    setupOther: "An admin needs to set up the advisor before it can answer.",
    setupLink: "Open ParamIO",
    unreachable: "I could not reach the advisor. Please try again.",
    add: "Add to dashboard",
    added: "Added to the dashboard",
    cards: "cards",
    addAll: "Add all to this dashboard",
    addedAll: "Added to this dashboard",
    create: "Create dashboard",
    created: "Dashboard created",
    sizes: { s: "small", m: "medium", l: "wide" },
  },
  bg: {
    hi: (name) => `Здравейте, аз съм ${name}`,
    intro: "Питайте ме за продажбите, поръчките, клиентите, продуктите и наличностите. Отговарям от вашите данни — за периода и филтрите на таблото, ако не кажете друго.",
    disclaimer: "AI може да греши: картите показват данните, които е използвал.",
    resume: "Продължи последния разговор",
    newChat: "Нов разговор",
    placeholder: "Попитайте за продажби, поръчки, продукти…",
    unavailable: "Съветникът не е достъпен в момента. Опитайте по-късно.",
    setupTitle: "Първо настройте съветника",
    setupAdmin: (model, key) => `Моделът му${model ? ` (${model})` : ""} още няма API ключ. Добавете ${key || "API ключа на модела"} в Admin → ParamIO и отворете съветника отново.`,
    setupOther: "Администратор трябва да настрои съветника, преди да може да отговаря.",
    setupLink: "Отвори ParamIO",
    unreachable: "Не успях да се свържа със съветника. Опитайте отново.",
    add: "Добави към таблото",
    added: "Добавено към таблото",
    cards: "карти",
    addAll: "Добави всички към това табло",
    addedAll: "Добавено към това табло",
    create: "Създай табло",
    created: "Таблото е създадено",
    sizes: { s: "малка", m: "средна", l: "широка" },
  },
}
const LANGUAGE_KEY = "store-advisor-language"

/** The language the advisor speaks: the one chosen before, else the site's. */
function initialLanguage() {
  try {
    const kept = localStorage.getItem(LANGUAGE_KEY)
    if (kept === "bg" || kept === "en") return kept
  } catch {}
  if (typeof window === "undefined") return "en"
  const site = document.documentElement.lang || window.location.pathname.split("/")[1] || navigator.language || ""
  return String(site).toLowerCase().startsWith("bg") ? "bg" : "en"
}

const TYPE_ICON = { metric: HashtagIcon, chart: ChartBarIcon, table: TableCellsIcon, segments: Bars3BottomLeftIcon }

/** A whole dashboard the advisor proposed: its cards listed, created or added with one click. */
function DashboardProposal({ proposal, catalog, onCreate, onAddAll, t }) {
  const [done, setDone] = useState(null)
  const labelOf = (card) =>
    card.title ||
    (card.type === "table" ? catalog?.tables?.find((t) => t.id === card.table)?.label : catalog?.metrics?.find((m) => m.id === card.metric)?.label) ||
    card.metric ||
    card.table
  return (
    <div className="rounded-xl border border-admin_bordered bg-admin_paper p-4 flex flex-col gap-3" data-testid="advisor-dashboard">
      <div className="flex items-center gap-2">
        <Squares2X2Icon className="w-5 h-5 text-admin_primary" />
        <span className="text-sm font-semibold text-admin_text">{proposal.name}</span>
        <span className="text-xs text-admin_text_secondary">· {proposal.cards.length} {t.cards}</span>
      </div>
      <ul className="flex flex-col gap-1 m-0 p-0 list-none">
        {proposal.cards.map((card) => {
          const Icon = TYPE_ICON[card.type] || HashtagIcon
          return (
            <li key={card.id} className="flex items-center gap-2 text-sm text-admin_text">
              <Icon className="w-4 h-4 shrink-0 text-admin_text_secondary" />
              <span className="truncate">{labelOf(card)}</span>
              <span className="ml-auto text-[11px] text-admin_text_secondary">{t.sizes[card.size] || t.sizes.s}</span>
            </li>
          )
        })}
      </ul>
      <div className="flex flex-wrap justify-end gap-2">
        {done ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-admin_success">
            <CheckIcon className="w-4 h-4" />
            {done}
          </span>
        ) : (
          <>
            <button type="button" onClick={() => {
                onAddAll(proposal)
                setDone(t.addedAll)
              }} className="h-8 px-3 rounded-full text-xs font-semibold text-admin_primary hover:bg-admin_primary/10" data-testid="advisor-dashboard-add">
              {t.addAll}
            </button>
            <button
              type="button"
              onClick={async () => {
                await onCreate(proposal)
                setDone(t.created)
              }}
              className="h-8 px-4 rounded-full text-xs font-semibold bg-admin_primary text-white"
              data-testid="advisor-dashboard-create"
            >
              {t.create}
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const Thinking = () => (
  <div className="flex items-center gap-1.5 px-1 py-2" data-testid="advisor-thinking">
    {[0, 1, 2].map((i) => (
      <span key={i} className="w-2 h-2 rounded-full bg-admin_primary/60 animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
    ))}
  </div>
)

/**
 * "Ask the advisor", the way Google Analytics' Advisor works: a chat beside
 * the dashboard that answers questions about the store from its real figures
 * — for the dashboard's period and filters unless told otherwise — and shows
 * a card for its answer, which the person can add to the dashboard.
 */
export default function Advisor({ scope, catalog, context, onAddCard, onCreateDashboard, onAddCards, name: brandName, icon = null }) {
  // the host's brand, when it has one (its logo), else the sparkles
  const Mark = ({ className }) => (icon ? <img src={icon} alt="" className={`${className} object-contain`} /> : <SparklesIcon className={`${className} text-admin_primary`} />)
  const [open, setOpen] = useState(false)
  const [state, setState] = useState(null) // { available, reason?, model?, keyName?, isAdmin, name, starters, session }
  // the host's name for it, else the agent instance's (Admin → Agents)
  const name = brandName || state?.name || "AI Advisor"
  // its AI model has no API key: it says so, and where an admin sets it up
  const notConfigured = state?.reason === "not_configured"
  // the language the advisor answers and suggests in; the person's words in the chat can still change it
  const [language, setLanguage] = useState(initialLanguage)
  const t = TEXT[language] || TEXT.en
  const chooseLanguage = (next) => {
    if (next === language) return
    setLanguage(next)
    try {
      localStorage.setItem(LANGUAGE_KEY, next)
    } catch {}
    // the starters are asked again, in the new language
    setState(null)
  }
  const [messages, setMessages] = useState([])
  const [sessionId, setSessionId] = useState(null)
  const [text, setText] = useState("")
  const [busy, setBusy] = useState(false)
  const listRef = useRef(null)
  const inputRef = useRef(null)
  // the chat's end, and the latest answer: what the scrolling aims at
  const endRef = useRef(null)
  const answerRef = useRef(null)

  const toEnd = (behavior = "smooth") => requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: "end", behavior }))

  /** The input grows with its lines (to about six, then it scrolls), and the chat stays at its end. */
  const fit = () => {
    const el = inputRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }

  useEffect(() => {
    if (!open || state) return
    AdvisorStart(scope, language)
      .then((res) => setState(res?.result ? res : { available: false, starters: [] }))
      .catch(() => setState({ available: false, starters: [] }))
  }, [open, scope, state === null, language])
  useEffect(() => setState(null), [scope])
  // a question and the thinking dots: the chat's end in sight; an answer: its start
  // (a long one with a card would otherwise open on the card's last row)
  useEffect(() => {
    const last = messages[messages.length - 1]
    if (busy || last?.role === "user") {
      toEnd()
      return
    }
    if (last?.role === "agent") requestAnimationFrame(() => answerRef.current?.scrollIntoView({ block: "start", behavior: "smooth" }))
  }, [messages, busy])
  useEffect(() => {
    fit()
    if (messages.length) toEnd("auto")
  }, [text])
  // the input takes the cursor once the advisor can answer — when it opens (after its
  // slide-in) and after every answer — so a question can be typed straight away
  useEffect(() => {
    if (!open) return
    setTimeout(() => toEnd("auto"), 350)
  }, [open])
  useEffect(() => {
    if (!open || !state?.available || busy) return
    const timer = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 350)
    return () => clearTimeout(timer)
  }, [open, state?.available, busy])

  const ask = async (question) => {
    const message = String(question || "").trim()
    if (!message || busy) return
    setText("")
    setMessages((list) => [...list, { role: "user", content: message }])
    setBusy(true)
    const res = await AdvisorAsk({ scope, sessionId, message, context, language }).catch(() => null)
    setBusy(false)
    if (!res?.result) {
      setMessages((list) => [...list, { role: "agent", content: t.unreachable, error: true }])
      return
    }
    setSessionId(res.sessionId)
    setMessages((list) => [...list, res.reply])
    // it lost its model's key since it opened: the notice says so
    if (res.reason === "not_configured") setState((s) => (s ? { ...s, available: false, reason: "not_configured" } : s))
  }

  const resume = () => {
    setMessages(state.session.messages || [])
    setSessionId(state.session.sessionId)
    // a resumed chat opens at its end, where it was left
    setTimeout(() => toEnd("auto"), 50)
  }
  const newChat = async () => {
    await AdvisorReset(scope).catch(() => {})
    setMessages([])
    setSessionId(null)
    setState((s) => (s ? { ...s, session: null } : s))
  }

  const lastAgent = [...messages].reverse().find((m) => m.role === "agent")

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 h-10 px-2.5 md:px-3 rounded-lg border border-admin_bordered bg-admin_paper text-sm font-semibold text-admin_text hover:border-admin_primary"
        title={name}
        data-testid="advisor-open"
      >
        <Mark className="w-5 h-5" />
        <span className="hidden md:inline">{name}</span>
      </button>

      <Modal
        isOpen={open}
        onClose={() => setOpen(false)}
        variant="admin_drawer_right"
        closeOnEscape
        className="max-w-[560px]"
        title={
          <span className="inline-flex items-center gap-2">
            <Mark className="w-6 h-6" />
            {name}
            <span className="px-1.5 py-0.5 rounded bg-admin_primary/10 text-admin_primary text-[10px] font-bold tracking-wide">BETA</span>
          </span>
        }
        // a new conversation, from the header's top-left corner (the close button has the right);
        // on a phone the title fills the header, so it sits beside the language switch instead
        headerContent={
          <button
            type="button"
            onClick={newChat}
            disabled={!messages.length || busy}
            className="absolute left-4 top-4 z-20 hidden md:inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-admin_primary hover:bg-admin_primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
            title={t.newChat}
            data-testid="advisor-new-chat"
          >
            <PlusIcon className="w-4 h-4" />
            {t.newChat}
          </button>
        }
        footerContent={
          <form
            className="flex items-end gap-2 p-3 border-t border-admin_bordered bg-admin_paper"
            onSubmit={(e) => {
              e.preventDefault()
              ask(text)
            }}
          >
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  ask(text)
                }
              }}
              placeholder={t.placeholder}
              disabled={state && !state.available}
              className="flex-1 min-h-[44px] max-h-[140px] overflow-y-auto resize-none px-4 py-2.5 rounded-2xl border border-admin_bordered bg-admin_paper text-sm text-admin_text outline-none focus:border-admin_primary"
              data-testid="advisor-input"
            />
            <button type="submit" disabled={!text.trim() || busy} className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-full bg-admin_primary text-white disabled:opacity-40" title="Send" data-testid="advisor-send">
              <PaperAirplaneIcon className="w-5 h-5" />
            </button>
          </form>
        }
      >
        <div ref={listRef} className="flex flex-col gap-4 p-4" data-testid="advisor-panel">
          {/* the language the advisor answers in: its answers and follow-ups follow it */}
          <div className="-mt-2 flex items-center justify-between gap-3">
            <span className="hidden md:inline text-[11px] text-admin_text_secondary">{t.disclaimer}</span>
            <button
              type="button"
              onClick={newChat}
              disabled={!messages.length || busy}
              className="md:hidden -ml-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-semibold text-admin_primary hover:bg-admin_primary/10 disabled:opacity-40 disabled:hover:bg-transparent"
              title={t.newChat}
              data-testid="advisor-new-chat-mobile"
            >
              <PlusIcon className="w-4 h-4" />
              {t.newChat}
            </button>
            <div className="shrink-0 inline-flex rounded-full border border-admin_bordered p-0.5" role="group" aria-label="Language" data-testid="advisor-language">
              {["en", "bg"].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => chooseLanguage(code)}
                  aria-pressed={language === code}
                  className={`h-6 px-2.5 rounded-full text-[11px] font-bold uppercase ${language === code ? "bg-admin_primary text-white" : "text-admin_text_secondary hover:text-admin_text"}`}
                  data-testid={`advisor-language-${code}`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          {!messages.length && (
            <div className="flex flex-col gap-4">
              {state?.session && (
                <button type="button" onClick={resume} className="flex items-center gap-3 rounded-xl bg-admin_neutral_1/60 px-4 py-3 text-left hover:bg-admin_neutral_1" data-testid="advisor-resume">
                  <ClockIcon className="w-6 h-6 text-admin_primary shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-admin_primary">{t.resume}</span>
                    <span className="block text-sm text-admin_text truncate">{state.session.title}</span>
                  </span>
                </button>
              )}
              <div>
                <div className="text-[22px] font-semibold text-admin_primary">{t.hi(name)}</div>
                <p className="text-sm text-admin_text mt-1 mb-0">{t.intro}</p>
              </div>
              {notConfigured && (
                <div className="rounded-lg border border-admin_warning/40 bg-admin_warning/10 px-4 py-3 text-sm text-admin_text" data-testid="advisor-setup-notice">
                  <div className="font-semibold">{t.setupTitle}</div>
                  <p className="mt-1 mb-0">{state.isAdmin ? t.setupAdmin(state.model, state.keyName) : t.setupOther}</p>
                  {state.isAdmin && (
                    <a href="/admin/paramio/params" className="mt-2 inline-block text-xs font-semibold text-admin_primary hover:underline">
                      {t.setupLink}
                    </a>
                  )}
                </div>
              )}
              {state && !state.available && !notConfigured && (
                <div className="rounded-lg bg-admin_error/10 px-4 py-3 text-sm text-admin_error">{t.unavailable}</div>
              )}
              <div className="flex flex-col items-end gap-2">
                {(notConfigured ? [] : state?.starters || []).map((q) => (
                  <button key={q} type="button" disabled={!state?.available} onClick={() => ask(q)} className="max-w-[90%] rounded-2xl bg-admin_neutral_1/70 px-4 py-2.5 text-sm text-admin_text text-left hover:bg-admin_primary/10 disabled:opacity-50" data-testid="advisor-starter">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) =>
            m.role === "user" ? (
              <div key={i} className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-admin_primary px-4 py-2.5 text-sm text-white whitespace-pre-wrap" data-testid="advisor-question">
                {m.content}
              </div>
            ) : (
              <div key={i} ref={m === lastAgent ? answerRef : undefined} className="flex flex-col gap-3 scroll-mt-4" data-testid="advisor-answer">
                <div className="flex gap-2.5">
                  <span className="w-7 h-7 shrink-0 rounded-full bg-admin_primary/10 inline-flex items-center justify-center">
                    <Mark className="w-4 h-4" />
                  </span>
                  <div className={`min-w-0 flex-1 ${m.error ? "text-admin_error" : ""}`}>
                    <Prose text={m.content} />
                  </div>
                </div>
                {m.dashboard && <DashboardProposal proposal={m.dashboard} catalog={catalog} onCreate={onCreateDashboard} onAddAll={onAddCards} t={t} />}
                {(m.cards || []).map((entry, j) => (
                  <AdvisorCard key={j} entry={entry} catalog={catalog} onAdd={onAddCard} t={t} />
                ))}
                {m === lastAgent && !busy && (m.suggestions || []).length > 0 && (
                  <div className="flex flex-col items-end gap-2">
                    {m.suggestions.map((q) => (
                      <button key={q} type="button" onClick={() => ask(q)} className="max-w-[90%] rounded-2xl border border-admin_bordered px-4 py-2 text-sm text-admin_text text-left hover:border-admin_primary hover:text-admin_primary" data-testid="advisor-suggestion">
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
          {busy && <Thinking />}
          <div ref={endRef} className="h-px" />
        </div>
      </Modal>
    </>
  )
}
